import { useEffect, useState } from "react";
import { ShieldCheck, FileSignature, ListChecks, Award, Package,
              Hash, AlertTriangle, CheckCircle2, Loader2, RefreshCw,
              Download, Eye } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useT } from "../lib/i18n";

/** Wave T — Insurance-Grade Documentation panel.
 *
 * Five sub-tabs per Capexara Process Industry_Module_Insurance_Documentation.md:
 *   • Audit trail   — query + chain integrity verification
 *   • Peer reviews  — assign + see pending
 *   • Certificates  — generate 5 cert types + download PDF
 *   • Packages      — Regulatory / PE-stamp / PI-insurance bundles
 *   • Numbering     — preview/allocate the next document number
 */
export default function DocumentationPanel({ projectId }) {
  const t = useT();
  const [tab, setTab] = useState("audit");
  if (!projectId) return null;
  const tabs = [
    { id: "audit",         label: "Audit trail",       Icon: ListChecks },
    { id: "reviews",       label: "Peer reviews",      Icon: FileSignature },
    { id: "certificates",  label: "Certificates",      Icon: Award },
    { id: "packages",      label: "Packages",          Icon: Package },
    { id: "numbering",     label: "Doc numbering",     Icon: Hash },
  ];
  return (
    <div data-testid="docs-panel" style={{ padding: 16,
                              background: "var(--pf-bg)", color: "var(--pf-text)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <ShieldCheck size={20} color="#F26B1F" />
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            {t("documentation_compliance", "Documentation & Compliance")}
          </div>
          <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>
            Insurance-grade audit trail · Peer-review workflow ·
            Regulatory packages · Tamper-evident hash chain
          </div>
        </div>
      </div>

      <div role="tablist" style={{ display: "flex", gap: 4,
              borderBottomStyle: "solid", borderBottomWidth: 1,
              borderBottomColor: "var(--pf-border)", marginBottom: 14 }}>
        {tabs.map(({ id, label, Icon }) => (
          <button key={id}
              data-testid={`docs-tab-${id}`}
              onClick={() => setTab(id)}
              style={{
                padding: "8px 14px", display: "flex", gap: 6,
                alignItems: "center", fontSize: 12,
                background: tab === id ? "rgba(242,107,31,0.15)" : "transparent",
                color: tab === id ? "#F26B1F" : "var(--pf-text)",
                borderTop: "none", borderLeft: "none", borderRight: "none",
                borderBottomStyle: "solid", borderBottomWidth: 2,
                borderBottomColor: tab === id ? "#F26B1F" : "transparent",
                cursor: "pointer", fontWeight: 600,
              }}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "audit"        && <AuditTab projectId={projectId} />}
      {tab === "reviews"      && <ReviewsTab projectId={projectId} />}
      {tab === "certificates" && <CertificatesTab projectId={projectId} />}
      {tab === "packages"     && <PackagesTab projectId={projectId} />}
      {tab === "numbering"    && <NumberingTab projectId={projectId} />}
    </div>
  );
}


function AuditTab({ projectId }) {
  const [items, setItems] = useState([]);
  const [chain, setChain] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const reload = async () => {
    setBusy(true); setErr("");
    try {
      const t = await api.get(`/projects/${projectId}/docs/audit/trail?limit=100`);
      setItems(t.data.items || []);
      const c = await api.get(`/projects/${projectId}/docs/audit/verify`);
      setChain(c.data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
    setBusy(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId]);

  return (
    <div data-testid="docs-audit">
      <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", marginBottom: 8 }}>
        <div data-testid="audit-chain-status" style={{ fontSize: 12, fontWeight: 600,
                                color: chain?.ok ? "#10B981" : (chain ? "#EF4444" : "var(--pf-muted)") }}>
          {chain
              ? (chain.ok
                    ? <><CheckCircle2 size={14} style={{ display: "inline-block", marginRight: 4 }} /> Hash chain OK · {chain.count} entries</>
                    : <><AlertTriangle size={14} style={{ display: "inline-block", marginRight: 4 }} /> Chain broken at seq {chain.broken_at}</>)
              : "Verifying chain…"}
        </div>
        <button data-testid="audit-refresh" onClick={reload} className="btn btn-ghost" style={{ fontSize: 12 }}>
          {busy ? <Loader2 size={14} className="pf-spin" /> : <RefreshCw size={14} />} Refresh
        </button>
      </div>
      {err && <div style={{ color: "#EF4444", fontSize: 11, marginBottom: 8 }}>⚠ {err}</div>}
      <div style={{ border: "1px solid var(--pf-border)", borderRadius: 6,
                          maxHeight: 400, overflowY: "auto" }}>
        {items.length === 0 && !busy && (
          <div style={{ padding: 16, textAlign: "center", color: "var(--pf-muted)", fontSize: 12 }}>
            No audit entries yet — every action you take in this project will be recorded here.
          </div>
        )}
        {items.map(e => (
          <div key={e.id} style={{ padding: "8px 12px",
                                      borderBottom: "1px solid var(--pf-border)",
                                      fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#F26B1F", fontWeight: 600 }}>#{e.seq} · {e.action_type}</span>
              <span style={{ color: "var(--pf-muted)" }}>{e.ts?.replace("T", " ").slice(0, 19)}</span>
            </div>
            <div style={{ color: "var(--pf-muted)", marginTop: 2 }}>
              user: {e.user_name || e.user_id || "—"} · ip: {e.ip || "—"}
            </div>
            <div style={{ marginTop: 4, color: "var(--pf-text)" }}>
              {JSON.stringify(e.details || {}).slice(0, 220)}
            </div>
            <div style={{ color: "var(--pf-muted)", fontSize: 10, marginTop: 2 }}>
              hash: {e.hash?.slice(0, 24)}…
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function ReviewsTab({ projectId }) {
  const [pending, setPending] = useState([]);
  const [calcId, setCalcId] = useState("");
  const [checkerId, setCheckerId] = useState("");
  const [discipline, setDiscipline] = useState("ST");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const reload = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/docs/peer-review/pending`);
      setPending(r.data.items || []);
    } catch (_) { /* ok */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId]);

  const assign = async () => {
    if (!calcId.trim() || !checkerId.trim()) {
      setErr("Both calculation id and checker id are required."); return;
    }
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await api.post(
        `/projects/${projectId}/docs/peer-review/${encodeURIComponent(calcId)}/assign`,
        { checker_id: checkerId, discipline, title });
      setMsg(`Assigned ${r.data.id} → ${r.data.checker_id}`);
      setCalcId(""); setCheckerId(""); setTitle("");
      reload();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
    setBusy(false);
  };

  return (
    <div data-testid="docs-reviews">
      <div className="pf-overline" style={{ marginBottom: 6 }}>Assign a peer review</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 100px", gap: 8 }}>
        <input data-testid="review-calc-id" className="pf-input"
            placeholder="Calc / drawing ID (e.g. CALC-B-301)"
            value={calcId} onChange={e => setCalcId(e.target.value)}
            style={{ fontSize: 12, padding: 6 }} />
        <input data-testid="review-checker-id" className="pf-input"
            placeholder="Checker user id" value={checkerId}
            onChange={e => setCheckerId(e.target.value)}
            style={{ fontSize: 12, padding: 6 }} />
        <select data-testid="review-discipline" className="pf-input"
            value={discipline} onChange={e => setDiscipline(e.target.value)}
            style={{ fontSize: 12, padding: 6 }}>
          {"PR PI ME ST CV EL IN FP AR PL HV PM".split(" ").map(d =>
              <option key={d} value={d}>{d}</option>)}
        </select>
      </div>
      <input data-testid="review-title" className="pf-input"
          placeholder="Review title (optional)" value={title}
          onChange={e => setTitle(e.target.value)}
          style={{ marginTop: 6, fontSize: 12, padding: 6, width: "100%" }} />
      <button data-testid="review-assign-btn" onClick={assign}
          disabled={busy} className="btn btn-primary"
          style={{ marginTop: 8, fontSize: 12 }}>
        {busy ? <Loader2 size={14} className="pf-spin" /> : <FileSignature size={14} />}
        Assign reviewer
      </button>
      {err && <div style={{ color: "#EF4444", fontSize: 11, marginTop: 6 }}>⚠ {err}</div>}
      {msg && <div style={{ color: "#10B981", fontSize: 11, marginTop: 6 }}>✓ {msg}</div>}

      <div className="pf-overline" style={{ marginTop: 14, marginBottom: 6 }}>
        Reviews assigned to me (pending)
      </div>
      <div style={{ border: "1px solid var(--pf-border)", borderRadius: 6 }}>
        {pending.length === 0 ? (
          <div style={{ padding: 12, fontSize: 12, color: "var(--pf-muted)" }}>
            No pending reviews assigned to you on this project.
          </div>
        ) : pending.map(r => (
          <div key={r.id} style={{ padding: 10, fontSize: 12,
                                      borderBottom: "1px solid var(--pf-border)" }}>
            <div style={{ fontWeight: 600 }}>{r.title}</div>
            <div style={{ color: "var(--pf-muted)", fontSize: 11 }}>
              {r.id} · calc: {r.calc_id} · disc: {r.discipline} · designer: {r.designer_id}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


const CERT_TYPES = [
  { id: "structural",     label: "Structural Stability" },
  { id: "nbc",            label: "NBC Compliance" },
  { id: "fire",           label: "Fire Safety" },
  { id: "environmental",  label: "Environmental Compliance" },
  { id: "green",          label: "Green Building (IGBC)" },
];


function CertificatesTab({ projectId }) {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [igbcPoints, setIgbcPoints] = useState("62");
  const [igbcGrade, setIgbcGrade] = useState("Gold");

  const reload = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/docs/certificates`);
      setList(r.data.items || []);
    } catch (_) { /* ok */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId]);

  const generate = async (type) => {
    setBusy(true); setErr("");
    try {
      const extras = type === "green"
          ? { points: igbcPoints, grade: igbcGrade } : {};
      await api.post(`/projects/${projectId}/docs/certificate/${type}`,
                          { extras });
      reload();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
    setBusy(false);
  };

  const downloadPdf = async (cert) => {
    try {
      const r = await api.get(
        `/projects/${projectId}/docs/certificate/${cert.id}/pdf`,
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (_) { /* ok */ }
  };

  return (
    <div data-testid="docs-certificates">
      <div className="pf-overline" style={{ marginBottom: 6 }}>Generate certificate</div>
      <div style={{ display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                          gap: 6 }}>
        {CERT_TYPES.map(c => (
          <button key={c.id}
              data-testid={`cert-gen-${c.id}`}
              onClick={() => generate(c.id)} disabled={busy}
              className="btn btn-ghost"
              style={{ fontSize: 12, justifyContent: "flex-start",
                          padding: "8px 10px" }}>
            <Award size={14} /> {c.label}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 6, display: "flex", gap: 8,
                          alignItems: "center", fontSize: 11,
                          color: "var(--pf-muted)" }}>
        IGBC params:
        <input data-testid="igbc-points" value={igbcPoints}
            onChange={e => setIgbcPoints(e.target.value)}
            placeholder="points" style={{ width: 60, padding: 4, fontSize: 11 }} />
        <input data-testid="igbc-grade" value={igbcGrade}
            onChange={e => setIgbcGrade(e.target.value)}
            placeholder="grade" style={{ width: 80, padding: 4, fontSize: 11 }} />
      </div>
      {err && <div style={{ color: "#EF4444", fontSize: 11, marginTop: 6 }}>⚠ {err}</div>}

      <div className="pf-overline" style={{ marginTop: 14, marginBottom: 6 }}>
        Issued certificates ({list.length})
      </div>
      <div style={{ border: "1px solid var(--pf-border)", borderRadius: 6 }}>
        {list.length === 0 ? (
          <div style={{ padding: 12, fontSize: 12, color: "var(--pf-muted)" }}>
            No certificates issued yet.
          </div>
        ) : list.map(c => (
          <div key={c.id} style={{ padding: 10, fontSize: 12,
                                      borderBottom: "1px solid var(--pf-border)",
                                      display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontWeight: 600 }}>{c.title}</div>
              <div style={{ color: "var(--pf-muted)", fontSize: 11,
                                  fontFamily: "'JetBrains Mono', monospace" }}>
                {c.doc_number} · cert: {c.id} · hash: {c.hash?.slice(0, 16)}…
              </div>
            </div>
            <button data-testid={`cert-pdf-${c.id}`}
                onClick={() => downloadPdf(c)} className="btn btn-ghost"
                style={{ fontSize: 11 }}>
              <Download size={13} /> PDF
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


const PACKAGE_TYPES = [
  { id: "regulatory",    label: "Regulatory submission",
      help: "Cover letter · application · plans · area statement · FSI · stability · NBC · fire NOC" },
  { id: "pe_stamp",      label: "PE stamp package",
      help: "Calc sheets · summary · code-list · stampable signature blocks" },
  { id: "pi_insurance",  label: "PI insurance package",
      help: "Design management plan · audit report · chain integrity proof" },
];


function PackagesTab({ projectId }) {
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const generate = async (type) => {
    setBusy(type); setErr("");
    try {
      const r = await api.post(`/projects/${projectId}/docs/package/${type}`,
                                          { notes: "" }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
    setBusy("");
  };

  return (
    <div data-testid="docs-packages">
      <div style={{ fontSize: 11, color: "var(--pf-muted)", marginBottom: 8 }}>
        Bundle the project's design artefacts into a regulatory or insurance-ready PDF.
      </div>
      {PACKAGE_TYPES.map(p => (
        <div key={p.id} style={{ display: "flex", gap: 12,
                                          alignItems: "center", padding: "10px 12px",
                                          border: "1px solid var(--pf-border)",
                                          borderRadius: 6, marginBottom: 6 }}>
          <Package size={16} color="#F26B1F" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{p.label}</div>
            <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>{p.help}</div>
          </div>
          <button data-testid={`pkg-gen-${p.id}`}
              onClick={() => generate(p.id)} disabled={!!busy}
              className="btn btn-primary" style={{ fontSize: 12 }}>
            {busy === p.id ? <Loader2 size={14} className="pf-spin" /> : <Download size={14} />}
            Generate
          </button>
        </div>
      ))}
      <button data-testid="pkg-compliance-report" className="btn btn-ghost"
          style={{ marginTop: 8, fontSize: 12 }}
          onClick={async () => {
            try {
              const r = await api.get(`/projects/${projectId}/docs/compliance-report`,
                                                  { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
              window.open(url, "_blank");
            } catch (_) { /* ok */ }
          }}>
        <Eye size={14} /> Generate compliance report (Design Audit)
      </button>
      {err && <div style={{ color: "#EF4444", fontSize: 11, marginTop: 8 }}>⚠ {err}</div>}
    </div>
  );
}


const DISCIPLINES = ["PR","PI","ME","ST","CV","EL","IN","FP","AR","PL","HV","PM"];
const DOC_TYPES   = ["PFD","PID","CAL","DWG","SPC","DSH","IDX","REP","CRT","MOM","TRM"];


function NumberingTab({ projectId }) {
  const [discipline, setDiscipline] = useState("ST");
  const [docType, setDocType] = useState("CAL");
  const [revision, setRevision] = useState("0");
  const [last, setLast] = useState(null);
  const [err, setErr] = useState("");

  const allocate = async () => {
    setErr("");
    try {
      const r = await api.get(
        `/projects/${projectId}/docs/numbering/next/${discipline}/${docType}?revision=${revision}`);
      setLast(r.data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    }
  };

  return (
    <div data-testid="docs-numbering">
      <div style={{ fontSize: 11, color: "var(--pf-muted)", marginBottom: 10 }}>
        Format: <strong>[Project]-[Discipline]-[Type]-[NNN]-[Revision]</strong>
        — every allocation is sequential and atomic; sequences cannot collide.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px 140px",
                          gap: 8, alignItems: "end" }}>
        <div>
          <div className="pf-overline" style={{ marginBottom: 4 }}>Discipline</div>
          <select data-testid="num-discipline"
              className="pf-input" style={{ fontSize: 12, padding: 6 }}
              value={discipline} onChange={e => setDiscipline(e.target.value)}>
            {DISCIPLINES.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <div className="pf-overline" style={{ marginBottom: 4 }}>Doc type</div>
          <select data-testid="num-type"
              className="pf-input" style={{ fontSize: 12, padding: 6 }}
              value={docType} onChange={e => setDocType(e.target.value)}>
            {DOC_TYPES.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <div className="pf-overline" style={{ marginBottom: 4 }}>Rev</div>
          <input data-testid="num-revision"
              className="pf-input" style={{ fontSize: 12, padding: 6 }}
              value={revision} onChange={e => setRevision(e.target.value)} />
        </div>
        <button data-testid="num-allocate-btn"
            className="btn btn-primary"
            onClick={allocate} style={{ fontSize: 12 }}>
          <Hash size={14} /> Allocate next number
        </button>
      </div>
      {err && <div style={{ color: "#EF4444", fontSize: 11, marginTop: 8 }}>⚠ {err}</div>}
      {last && (
        <div data-testid="num-result" style={{ marginTop: 12, padding: 12,
                                background: "var(--pf-card)",
                                border: "1px solid var(--pf-border)",
                                borderRadius: 6,
                                fontFamily: "'JetBrains Mono', monospace",
                                fontSize: 14, color: "#F26B1F" }}>
          {last.doc_number}
        </div>
      )}
    </div>
  );
}
